import { showErrorMessage } from 'components/Helpers/Toasts';
import { mapCartItem, useRefetchCartInfo } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { AddToCartMutationApi, useAddToCartMutationApi } from 'graphql/generated';
import { useLatest } from 'hooks/ui/useLatest';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { GtmListNameType, GtmMessageOriginType } from 'types/gtm';
import { onChangeCartItemGtmEventHandler } from 'utils/Gtm/EventHandlers';
import { useGtmCartEventInfo } from 'utils/Gtm/Gtm';

type AddToCartAction = (
    productUuid: string,
    listIndex: number | null,
    quantity: number,
    gtmListName: GtmListNameType,
    isAbsoluteQuantity?: boolean,
) => Promise<AddToCartMutationApi['AddToCart'] | null>;

export const useAddToCart = (
    gtmMessageOrigin: GtmMessageOriginType,
    gtmProductListName: GtmListNameType,
): [AddToCartAction, boolean] => {
    const [{ fetching }, addToCart] = useAddToCartMutationApi();
    const refetchCartInfo = useRefetchCartInfo();
    const { cartUuid, defaultStoreUuid } = usePersistStore((s) => s);
    const { currencyCode, url } = useDomainConfig();
    const { t } = useTranslation();
    const gtmCartEventInfo = useGtmCartEventInfo();
    const gtmCart = useLatest(gtmCartEventInfo.cart);
    const { updateCartUuid } = usePersistStore((s) => s);

    const addToCartAction = useCallback<AddToCartAction>(
        async (productUuid, listIndex, quantity, gtmListName, isAbsoluteQuantity = false) => {
            const addToCartActionResult = await addToCart(
                {
                    input: { cartUuid, productUuid, quantity, isAbsoluteQuantity, productCatnum: null },
                    defaultStoreUuid,
                    selectedStoreUuid: null,
                },
                {
                    requestPolicy: 'network-only',
                },
            );
            updateCartUuid(addToCartActionResult.data?.AddToCart.cart.uuid ?? null);

            // EXTEND ADDING TO CART HERE
            if (addToCartActionResult.error !== undefined) {
                showErrorMessage(t('Unable to add product to cart'), gtmMessageOrigin);

                return null;
            }

            const addToCartResult = addToCartActionResult.data?.AddToCart;

            if (addToCartResult === undefined) {
                return null;
            }

            dispatchBroadcastChannel({ name: 'refetchCart' });

            const addedCartItem = addToCartResult.addProductResult.cartItem;
            const isProductGroupMixed = addToCartResult.addProductResult.isProductGroupMixed;
            const isVoucherTypesMixed = addToCartResult.addProductResult.isVoucherTypesMixed;

            if (isProductGroupMixed === true) {
                showErrorMessage(
                    t('It is not possible to combine a gift voucher with regular goods.'),
                    gtmMessageOrigin,
                );
            }

            if (isVoucherTypesMixed === true) {
                showErrorMessage(
                    t('It is not possible to combine physical and electronic gift vouchers.'),
                    gtmMessageOrigin,
                );
            }

            if (addedCartItem === null) {
                return addToCartResult;
            }

            dispatchBroadcastChannel({ name: 'refetchCart' });
            refetchCartInfo();

            const notOnStockQuantity = addToCartResult.addProductResult.notOnStockQuantity;

            if (notOnStockQuantity > 0) {
                showErrorMessage(
                    t(
                        'You have the maximum available amount in your cart, you cannot add more (total {{ quantity }} {{ unitName }})',
                        {
                            quantity: addedCartItem.quantity,
                            unitName: addedCartItem.product.unit.name,
                        },
                    ),
                    gtmMessageOrigin,
                );
            }

            const quantityDifference = addToCartResult.addProductResult.quantityDifference;
            const absoluteEventValue =
                Number.parseFloat(addedCartItem.product.price.priceWithoutVat) * Math.abs(quantityDifference);
            const absoluteEventValueWithTax =
                Number.parseFloat(addedCartItem.product.price.priceWithVat) * Math.abs(quantityDifference);

            setTimeout(() => {
                onChangeCartItemGtmEventHandler(
                    gtmCart.current,
                    mapCartItem(addedCartItem, currencyCode),
                    currencyCode,
                    absoluteEventValue,
                    absoluteEventValueWithTax,
                    listIndex,
                    quantityDifference,
                    gtmProductListName,
                    url,
                );
            }, 0);

            return addToCartResult;
        },
        [
            addToCart,
            cartUuid,
            defaultStoreUuid,
            updateCartUuid,
            refetchCartInfo,
            t,
            gtmMessageOrigin,
            gtmCart,
            currencyCode,
            url,
        ],
    );

    return [addToCartAction, fetching];
};
